import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import bookingChangesApi from '@/features/dashboard/bookingChanges/api/bookingChangesApi.ts';
import { bookingChangesApiQueryKeys } from '@/features/dashboard/bookingChanges/api/bookingChangesApi.queryKeys.ts';
import { bookingChangePublicMapper } from '@/features/dashboard/bookingChanges/types/BookingChangePublicDto.mapper.ts';

interface RejectPublicChangeParams {
  changeId: string;
  /** Wartości formularza po walidacji — komentarz już przycięty. */
  form: { comment: string };
}

/**
 * Odrzucenie zmian przez klienta („Odrzucam zmiany”), z opcjonalnym komentarzem,
 * który organizator widzi przy paczce.
 *
 * To stan **końcowy**: `BookingChange.reject` ustawia `REJECTED`, paczki nie da
 * się poprawić i wysłać ponownie — organizator przygotowuje nową.
 */
const useRejectPublicChange = (orgSlug: string, token: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ changeId, form }: RejectPublicChangeParams) =>
      bookingChangesApi.rejectPublicChange(
        orgSlug,
        changeId,
        token,
        bookingChangePublicMapper.toRejectRequest(form),
      ),
    onSuccess: (change) => {
      queryClient.invalidateQueries({
        queryKey: bookingChangesApiQueryKeys.publicChange(orgSlug, token),
      });
      toast.success('Zmiany zostały odrzucone', {
        description: change.rejectionComment
          ? 'Organizator zobaczy Twój komentarz przy rezerwacji.'
          : 'Organizator zobaczy odrzucenie przy rezerwacji.',
      });
    },
    onError: (error: Error) => {
      toast.error('Nie udało się wysłać odpowiedzi', {
        description: error.message,
      });
    },
  });
};

export default useRejectPublicChange;
